import React, { useState, useEffect } from 'react';
import { useBrand } from '../../../app/config/brand';
import { PageTitle } from '../../../shared/components/ui/PageTitle';
import { Card } from '../../../shared/components/ui/Card';
import { Button } from '../../../shared/components/ui/Button';
import { Icons } from '../../../shared/icons/Icons';
import { useAttorneyApi } from '../../../api/useAttorneyApi';
import type { AccountAttorney } from '../../../api/useAttorneyApi';
import AttorneyFormModal from '../components/AttorneyFormModal';

const AttorneyRosterPage: React.FC = () => {
  const brand = useBrand();
  const { getAttorneys, deleteAttorney, loading, error } = useAttorneyApi();
  const [attorneys, setAttorneys] = useState<AccountAttorney[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAttorney, setEditingAttorney] = useState<AccountAttorney | undefined>();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    getAttorneys()
      .then(data => setAttorneys(data))
      .catch(() => {});
  }, [getAttorneys]);

  const refreshAttorneys = async () => {
    try {
      const data = await getAttorneys();
      setAttorneys(data);
    } catch {
      // error state handled by hook
    }
  };

  const handleAdd = () => {
    setEditingAttorney(undefined);
    setShowModal(true);
  };

  const handleEdit = (attorney: AccountAttorney) => {
    setEditingAttorney(attorney);
    setShowModal(true);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingAttorney(undefined);
    refreshAttorneys();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAttorney(undefined);
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteAttorney(deleteConfirmId);
      setDeleteConfirmId(null);
      refreshAttorneys();
    } catch {
      // error state handled by hook
    }
  };

  const handleDeleteFromModal = async (id: string) => {
    try {
      await deleteAttorney(id);
      setShowModal(false);
      setEditingAttorney(undefined);
      refreshAttorneys();
    } catch {
      // error state handled by hook
    }
  };

  const attorneyToDelete = deleteConfirmId ? attorneys.find(a => a.id === deleteConfirmId) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageTitle>
          <span data-testid="attorney-roster-heading">Attorneys</span>
        </PageTitle>
        <Button data-testid="add-attorney-button" onClick={handleAdd} icon={Icons.Plus}>
          Add Attorney
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: brand.colors.error }}>
          {error}
        </div>
      )}

      <Card padding={false}>
        {loading && attorneys.length === 0 ? (
          <div className="text-center py-12" style={{ color: brand.colors.textLight }}>
            Loading attorneys...
          </div>
        ) : attorneys.length === 0 ? (
          <div data-testid="attorney-list" className="text-center py-12">
            <Icons.User className="w-12 h-12 mx-auto mb-4" style={{ color: brand.colors.textMuted }} />
            <p className="text-lg mb-2" style={{ color: brand.colors.text }}>No attorneys yet</p>
            <p className="text-sm mb-6" style={{ color: brand.colors.textLight }}>
              Add attorneys to your roster for quick access during eFiling.
            </p>
            <Button data-testid="add-attorney-button" onClick={handleAdd} icon={Icons.Plus}>
              Add Attorney
            </Button>
          </div>
        ) : (
          <div data-testid="attorney-list">
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: brand.colors.bgLight }}>
                <tr className="border-b" style={{ borderColor: brand.colors.border }}>
                  {['Name', 'Bar Number', 'Firm', 'Email', 'Phone', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-medium" style={{ color: brand.colors.secondary }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attorneys.map((attorney) => (
                  <tr
                    key={attorney.id}
                    data-testid={`attorney-row-${attorney.id}`}
                    className="border-b hover:bg-gray-50"
                    style={{ borderColor: brand.colors.border }}
                  >
                    <td className="px-4 py-3" style={{ color: brand.colors.text }}>
                      {attorney.firstName} {attorney.middleName ? `${attorney.middleName} ` : ''}{attorney.lastName}
                      {attorney.suffix ? `, ${attorney.suffix}` : ''}
                    </td>
                    <td className="px-4 py-3" style={{ color: brand.colors.text }}>{attorney.barNumber}</td>
                    <td className="px-4 py-3" style={{ color: brand.colors.textLight }}>{attorney.firmName || '-'}</td>
                    <td className="px-4 py-3" style={{ color: brand.colors.textLight }}>{attorney.email || '-'}</td>
                    <td className="px-4 py-3" style={{ color: brand.colors.textLight }}>{attorney.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          data-testid={`edit-attorney-${attorney.id}`}
                          onClick={() => handleEdit(attorney)}
                          className="px-3 py-1 rounded-md text-sm transition-colors hover:bg-gray-100"
                          style={{ color: brand.colors.link }}
                        >
                          Edit
                        </button>
                        <button
                          data-testid={`delete-attorney-${attorney.id}`}
                          onClick={() => handleDeleteClick(attorney.id)}
                          className="px-3 py-1 rounded-md text-sm transition-colors hover:bg-red-50"
                          style={{ color: brand.colors.error }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {deleteConfirmId && attorneyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" role="button" tabIndex={0} aria-label="Close dialog" onClick={() => setDeleteConfirmId(null)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDeleteConfirmId(null); } }} />
          <div className="relative rounded-xl shadow-lg p-6" style={{ backgroundColor: brand.colors.bgCard }}>
            <h3 className="text-lg font-semibold mb-2" style={{ color: brand.colors.secondary }}>Delete Attorney</h3>
            <p className="text-sm mb-4" style={{ color: brand.colors.text }}>
              Are you sure you want to delete {attorneyToDelete.firstName} {attorneyToDelete.lastName} ({attorneyToDelete.barNumber})?
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
              <Button onClick={handleDeleteConfirm} style={{ backgroundColor: brand.colors.error }}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <AttorneyFormModal
          attorney={editingAttorney}
          onSave={handleModalSave}
          onClose={handleModalClose}
          onDelete={editingAttorney ? handleDeleteFromModal : undefined}
        />
      )}
    </div>
  );
};

export default AttorneyRosterPage;
